import { ChangeDetectionStrategy, Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { DEFAULT_CURRENCY_CODE } from '@constants/currency';
import { BillItemPendingModel, PaymentBillItemPendingModel } from '@data-layer/models/payments';
import { enumBillItemDescription, enumBillItemProductCode } from '@data-layer/enums/payments';
import { ProductDetailsModel } from '@data-layer/models/common';
import { IAppLoaderConfig } from '@shared/request-loader/interfaces';

@Component({
  selector: 'app-lcl-payment-card',
  template: `
    <div class="payment-breakdown" *ngIf="!isBillLoading; else loader">
      <div class="payment-breakdown-content" [ngClass]="{ 'is-bordered': isBooking }">
        <div class="payment-breakdown-header">
          <div class="payment-breakdown-header-sum" *ngIf="!isMobileView">
            <p>
              {{ 'LABELS.ESTIMATED_TOTAL_PRICE' | translate }}
            </p>

            <span class="total-sum">{{ totalSum | currency : DEFAULT_CURRENCY_CODE : 'symbol' : '1.2-2' }}</span>
          </div>

          <div class="payment-breakdown-header-text">
            <h3>
              {{ 'LABELS.PAYMENT_BREAKDOWN' | translate }}
            </h3>

            <p class="more-collapse" [ngClass]="{ collapsed: isCollapsed }" *ngIf="isMobileView" (click)="isCollapsed = !isCollapsed">
              {{ 'LABELS.MORE' | translate }}
              <img src="assets/icons/icon_chevron_up.svg" />
            </p>
          </div>
        </div>

        <app-lcl-payment-items
          [pendingBills]="pendingBills"
          [pendingMarineInsuranceBills]="pendingMarineInsuranceBills"
          [productsByCompany]="productsByCompany"
          [isHasISF]="isHasISF"
          [isHasES]="isHasES"
          [isBillLoading]="isBillLoading"
          [isBooking]="isBooking"
          [isMobileView]="isMobileView"
          [isCollapsed]="isCollapsed"
          [chargesText]="chargesText"
        ></app-lcl-payment-items>
      </div>
    </div>

    <ng-template #loader>
      <loading-spinner [show]="true" [config]="loaderConfig"></loading-spinner>
    </ng-template>
  `,
  styleUrls: ['./lcl-payment-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LclPaymentCardComponent implements OnChanges {
  public _pendingBills: BillItemPendingModel[];

  public get pendingBills(): BillItemPendingModel[] {
    return this._pendingBills || [];
  }

  @Input()
  public set pendingBills(value: BillItemPendingModel[]) {
    this._pendingBills = value;
  }

  @Input() public pendingMarineInsuranceBills: PaymentBillItemPendingModel;
  @Input() public productsByCompany: ProductDetailsModel[];
  @Input() public isHasISF: boolean;
  @Input() public isHasES: boolean;
  @Input() public isHasExportLicense: boolean;
  @Input() public isBillLoading: boolean;
  @Input() public isBooking: boolean;
  @Input() public isMobileView: boolean;
  @Input() public isCollapsed: boolean;
  @Input() public chargesText: string;

  public DEFAULT_CURRENCY_CODE: typeof DEFAULT_CURRENCY_CODE = DEFAULT_CURRENCY_CODE;

  public loaderConfig: IAppLoaderConfig = {
    primaryColour: '#009999',
    secondaryColour: '#03bcbe',
    tertiaryColour: '#03bcbe',
    backdropBackgroundColour: '#ffffff',
  };

  private ORIGIN_BILL_ITEMS: enumBillItemProductCode[] = [
    enumBillItemProductCode.ORIGIN_CBM,
    enumBillItemProductCode.ORIGIN_HBL,
    enumBillItemProductCode.LOADING_FEES,
  ];

  private FREIGHT_BILL_ITEMS: enumBillItemProductCode[] = [enumBillItemProductCode.FREIGHT];

  private DESTINATION_BILL_ITEMS: enumBillItemProductCode[] = [
    enumBillItemProductCode.DESTINATION_CBM,
    enumBillItemProductCode.DESTINATION_HBL,
    enumBillItemProductCode.UNLOADING_FEES,
  ];

  private COLLECTION_FEE_BILL_ITEMS: enumBillItemProductCode[] = [enumBillItemProductCode.COLLECTION_FEE];

  private EXPORT_LICENSE_ITEM: BillItemPendingModel = {
    id: null,
    timestamp: null,
    paymentType: 0,
    referenceNumber: null,
    description: enumBillItemDescription.EXPORT_LICENSE,
    notes: null,
    quantity: 1,
    productCode: enumBillItemProductCode.EXPORT_LICENSE,
    price: 0,
    discountAmount: 0,
    salesTaxAmount: 0,
    discountGroupId: null,
    internalQuantities: null,
  };

  constructor() {}

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.isHasExportLicense) {
      this.EXPORT_LICENSE_ITEM.price = this.exportLicenseProductPrice;
      const pendingBillsWithoutExportLicense = this.pendingBills.filter(
        (item: BillItemPendingModel) => item.productCode !== enumBillItemProductCode.EXPORT_LICENSE,
      );
      this.pendingBills = changes.isHasExportLicense.currentValue
        ? [...pendingBillsWithoutExportLicense, this.EXPORT_LICENSE_ITEM]
        : pendingBillsWithoutExportLicense;
    }
  }

  public get mainBillItems(): BillItemPendingModel[] {
    return [...this.originBillItems, ...this.freightBillItems, ...this.destinationBillItems, ...this.additionalBillItems];
  }

  public get originBillItems(): BillItemPendingModel[] {
    return (
      this.pendingBills.filter((item: BillItemPendingModel) => this.ORIGIN_BILL_ITEMS.includes(item.productCode) && item.price > 0) || []
    );
  }

  public get freightBillItems(): BillItemPendingModel[] {
    return (
      this.pendingBills.filter((item: BillItemPendingModel) => this.FREIGHT_BILL_ITEMS.includes(item.productCode) && item.price > 0) || []
    );
  }

  public get destinationBillItems(): BillItemPendingModel[] {
    return (
      this.pendingBills.filter((item: BillItemPendingModel) => this.DESTINATION_BILL_ITEMS.includes(item.productCode) && item.price > 0) ||
      []
    );
  }

  public get additionalBillItems(): BillItemPendingModel[] {
    return (
      this.pendingBills.filter(
        (item: BillItemPendingModel) =>
          ![
            ...this.ORIGIN_BILL_ITEMS,
            ...this.FREIGHT_BILL_ITEMS,
            ...this.DESTINATION_BILL_ITEMS,
            ...this.COLLECTION_FEE_BILL_ITEMS,
          ].includes(item.productCode) && item.price > 0,
      ) || []
    );
  }

  public get collectionFeeBillItems(): BillItemPendingModel[] {
    return (
      this.pendingBills.filter(
        (item: BillItemPendingModel) => this.COLLECTION_FEE_BILL_ITEMS.includes(item.productCode) && item.price > 0,
      ) || []
    );
  }

  public get isfProductPrice(): number {
    return (
      (this.productsByCompany || []).find((item: ProductDetailsModel) => item.productCode === enumBillItemProductCode.ISF)?.product
        ?.fixedPrice || 0
    );
  }

  public get esProductPrice(): number {
    return (
      (this.productsByCompany || []).find((item: ProductDetailsModel) => item.productCode === enumBillItemProductCode.BROKER_ENTRY)?.product
        ?.fixedPrice || 0
    );
  }

  public get exportLicenseProductPrice(): number {
    return (
      (this.productsByCompany || []).find((item: ProductDetailsModel) => item.productCode === enumBillItemProductCode.EXPORT_LICENSE)
        ?.product?.fixedPrice || 0
    );
  }

  public get totalLCL(): number {
    return (this.mainBillItems || []).reduce((acc: number, current: BillItemPendingModel) => acc + current.price * current.quantity, 0);
  }

  public get totalAdditional(): number {
    let result: number = 0;
    const totalMarineInsuranceValue: number = this.totalMarineInsurance;

    if (this.isHasISF) {
      result += this.isfProductPrice;
    }

    if (this.isHasES) {
      result += this.esProductPrice;
    }

    if (totalMarineInsuranceValue > 0) {
      result += totalMarineInsuranceValue;
    }

    return result;
  }

  public get totalCollectionFee(): number {
    return (this.collectionFeeBillItems || []).reduce((acc: number, curr: BillItemPendingModel) => acc + curr.price, 0) || 0;
  }

  public get totalMarineInsurance(): number {
    return (this.pendingMarineInsuranceBills?.items || []).reduce((acc: number, curr: BillItemPendingModel) => acc + curr.price, 0) || 0;
  }

  public get totalSum(): number {
    return this.totalLCL + this.totalAdditional + this.totalCollectionFee || 0;
  }
}
